import { AuthSessionResult, makeRedirectUri } from 'expo-auth-session';
import * as Google from 'expo-auth-session/providers/google';
import * as Crypto from 'expo-crypto';
import { maybeCompleteAuthSession } from 'expo-web-browser';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { Platform } from 'react-native';

import { ActionMap } from '@src/types/actionmap';

maybeCompleteAuthSession();

const enum AuthStateActionType {
  prompt = 'PROMPT',
  setResponse = 'SET_RESPONSE',
  setUserInfo = 'SET_USER_INFO',
  resetUserInfo = 'RESET_USER_INFO',
}
type UserInfo = {
  email: string;
  given_name: string;
  family_name: string;
  provider?: string;
  id: string;
  fromSignUp?: boolean;
  identity_token?: string;
};

type AuthState = {
  loading: boolean;
  response?: AuthSessionResult;
  userInfo?: UserInfo;
  clearUserInfo?: () => void;
  setUserDetail?: (value: any) => void;
};

const initialAuthState: AuthState = {
  loading: false,
};

type AuthStatePayload = {
  [AuthStateActionType.prompt]: never;
  [AuthStateActionType.setResponse]: Required<AuthState>['response'];
  [AuthStateActionType.setUserInfo]: Required<AuthState>['userInfo'];
  [AuthStateActionType.resetUserInfo]: Required<AuthState>['userInfo'];
};

type AuthStateActions =
  ActionMap<AuthStatePayload>[keyof ActionMap<AuthStatePayload>];

const authStateReducer = (
  state: AuthState,
  action: AuthStateActions
): AuthState => {
  switch (action.type) {
    case AuthStateActionType.prompt: {
      return {
        ...state,
        loading: true,
        response: undefined,
        userInfo: undefined,
      };
    }
    case AuthStateActionType.setResponse: {
      return {
        ...state,
        loading: false,
        response: action.payload,
      };
    }
    case AuthStateActionType.setUserInfo: {
      return {
        ...state,
        loading: false,
        userInfo: action.payload,
      };
    }
    case AuthStateActionType.resetUserInfo: {
      return {
        ...state,
        loading: false,
        userInfo: undefined,
        response: null,
      };
    }
    default: {
      return state;
    }
  }
};

type GoogleAuthContext = AuthState & {
  prompt: () => void;
};

const googleAuthContext = createContext<GoogleAuthContext | undefined>(
  undefined
);

export const GoogleAuthProvider: React.FC = ({ children }) => {
  const [authState, dispatch] = useReducer(authStateReducer, initialAuthState);
  const { Provider } = googleAuthContext;
  const redirectUri = makeRedirectUri();
  // TA-10975: on web the default responseType (no clientSecret -> implicit
  // 'token') never includes an id_token, no matter what scopes are requested
  // -- only a responseType that names 'id_token' does. A combined type keeps
  // the existing accessToken (still used for the /userinfo/v2/me call below)
  // while also returning the id_token the backend can verify. The library
  // only auto-generates a nonce when responseType is the bare IdToken enum
  // value (strict equality check), not this combined string, so one is
  // supplied explicitly -- Google requires it whenever 'id_token' is present.
  // The same strict check is what turns PKCE off for implicit flows, so it
  // has to be disabled here too: Google rejects an implicit request carrying
  // code_challenge_method with "Error 400: invalid_request".
  // Native keeps the library default (code + PKCE): its auto code exchange
  // already returns an idToken, and Google's iOS/Android OAuth clients do not
  // support the implicit flow at all.
  const nonce = useMemo(() => Crypto.randomUUID(), []);
  const webIdTokenConfig: Partial<Google.GoogleAuthRequestConfig> =
    Platform.OS === 'web'
      ? {
          responseType: 'id_token token',
          usePKCE: false,
          extraParams: { nonce },
        }
      : {};

  const [_request, googleResponse, promptAsync] = Google.useAuthRequest({
    expoClientId: process.env.G_AUTH_EXPO_CLIENT_ID,
    iosClientId: process.env.G_AUTH_IOS_CLIENT_ID,
    androidClientId: process.env.G_AUTH_ANDROID_CLIENT_ID,
    webClientId: process.env.G_AUTH_WEB_CLIENT_ID,
    redirectUri,
    ...webIdTokenConfig,
  });
  const { loading, userInfo, response } = authState;

  useEffect(() => {
    if (loading) {
      promptAsync();
    }
  }, [loading]);

  useEffect(() => {
    if (!googleResponse) return;
    if (googleResponse.type === 'success') {
      getUserInfo(
        googleResponse.authentication?.accessToken ?? '',
        // TA-10975: the ID token (present alongside the access token once
        // 'openid' is in scope, as the default Google provider scopes are)
        // is what the backend can actually verify server-side -- forwarded
        // through so verify_user can restore a real proof-of-identity check.
        googleResponse.authentication?.idToken ?? ''
      );
    } else {
      clearUserInfo();
    }
  }, [googleResponse]);

  const getUserInfo = async (token: string, idToken: string) => {
    const userInfo = `/userinfo/v2/me`;
    const googleEndpoint = `${process.env.G_SIGN_IN_URL}${userInfo}`;
    try {
      const responseGoogle = await fetch(googleEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const user = await responseGoogle.json();
      if (user?.error?.code === 401) {
        console.log('Google OAuth Error', user?.error?.message);
        clearUserInfo();
      } else {
        dispatch({
          type: AuthStateActionType.setResponse,
          payload: { ...googleResponse, provider: 'Google' },
        });
        dispatch({
          type: AuthStateActionType.setUserInfo,
          payload: { ...user, provider: 'Google', identity_token: idToken },
        });
      }
    } catch (error) {
      console.error('error', error);
      clearUserInfo();
    }
  };

  const clearUserInfo = () => {
    dispatch({
      type: AuthStateActionType.resetUserInfo,
      payload: {},
      response: null,
      userInfo: undefined,
    });
  };
  const setUserDetail = (value: UserInfo) => {
    dispatch({
      type: AuthStateActionType.setUserInfo,
      payload: value,
    });
  };

  const value = useMemo(
    () => ({
      prompt: () => dispatch({ type: AuthStateActionType.prompt }),
      userInfo,
      loading,
      response,
      clearUserInfo,
      setUserDetail,
    }),
    [userInfo, loading, response, clearUserInfo, setUserDetail]
  );

  return <Provider value={value}>{children}</Provider>;
};

const useGoogleAuth = (): GoogleAuthContext => {
  const context = useContext(googleAuthContext);
  if (context === undefined) {
    throw new Error('useGoogleAuth must be used within a Provider');
  }
  return context;
};

export { useGoogleAuth };
